// The "never wipe a field" row builder (#5201, epic #5200 D6) — what one change to a
// purpose's pin writes.
//
// A re-pin REPLACES the whole `llm_routes` row: `upsertLlmRoute`
// (`packages/db/src/queries/llm-routes.ts`) writes every pin axis the body leaves out
// as null. So a UI that sends only the field it changed silently clears the others —
// pick a new model, lose the key pin and the reasoning effort. The fix is one rule
// in one place: the body is always the FULL intended row, built from the current row
// plus the change.
//
// Two server rules shape the answer, and both are applied here rather than left to a
// 4xx:
//   - `reasoning_effort` needs a pinned provider AND model (`reasoningRefusal`,
//     `apps/api/src/deps/llm-routes.ts`). A change that leaves either unpinned drops the
//     effort and REPORTS it in `cleared`, so the page can say so instead of guessing.
//   - a route pins at least one of key_label / provider / model. A change that leaves
//     none pinned is a DELETE, never an empty PUT.
//
// One product rule too: a picker-chosen model OWNS its provider. Every row the picker
// writes is `{provider, model}`, so Automatic on it clears both — a leftover
// provider-only pin would keep the resolver biased to the old provider
// (`MATCH_BAND_WEIGHTS.routeProvider` outranks the personal and tier bands) while the
// picker shows "Automatic". The dropped provider is reported in `cleared`. A row that
// pins a provider with NO model is a deliberate provider limit and Automatic leaves it.
// Accepted loss: a provider limit followed by a same-provider model pick is
// indistinguishable from a plain pick, so Automatic drops the limit too.
//
// Pure: the current row and the change in, the write out. No I/O.

import type { BYOKProvider } from '../entities/byok';
import type { ReasoningEffort } from './reasoning-effort';

/** The four columns a PUT replaces, in the wire's spelling. */
export const ROUTE_FIELDS = ['key_label', 'provider', 'model', 'reasoning_effort'] as const;
export type RouteField = (typeof ROUTE_FIELDS)[number];

/**
 * One purpose's stored pin — the four replaceable columns of an `llm_routes` row, as
 * the route list serves them (`LlmRouteView`). `provider` is text, like the column.
 */
export type RouteRow = {
  readonly key_label: string | null;
  readonly provider: string | null;
  readonly model: string | null;
  readonly reasoning_effort: ReasoningEffort | null;
};

/**
 * One edit a person makes to a purpose's pin.
 *
 * - `model`: pick a model OPTION. An option is a `(provider, model)` pair, so both are set.
 * - `automatic`: hand the model back to the resolver — the model clears, and the provider
 *   with it when a model was pinned (the pick set both); a key pin, or a provider-only
 *   limit, stays and keeps the row.
 * - `keyLabel` / `provider` / `effort`: set or clear (`null`) that one field.
 */
export type RouteChange =
  | { readonly kind: 'model'; readonly provider: BYOKProvider; readonly model: string }
  | { readonly kind: 'automatic' }
  | { readonly kind: 'keyLabel'; readonly keyLabel: string | null }
  | { readonly kind: 'provider'; readonly provider: BYOKProvider | null }
  | { readonly kind: 'effort'; readonly effort: ReasoningEffort | null };

/**
 * The PUT body — the full intended row in the shape `PUT /v1/llm-routes/…` takes: every
 * pinned axis present, an unpinned one ABSENT (the edge refuses `null` on the three
 * axes, and the replace writes an absent one null), `reasoning_effort` always stated.
 */
export type RoutePutBody = {
  readonly key_label?: string;
  readonly provider?: string;
  readonly model?: string;
  readonly reasoning_effort: ReasoningEffort | null;
};

/**
 * The write one change needs. `cleared` names every field the change emptied that it
 * did not itself target — the effort a model-less row cannot keep, the provider
 * Automatic drops with the model — so the page can tell the person what went, rather
 * than dropping it silently.
 */
export type NextRouteRow =
  | { readonly op: 'put'; readonly body: RoutePutBody; readonly cleared: readonly RouteField[] }
  | { readonly op: 'delete'; readonly cleared: readonly RouteField[] };

const NO_ROW: RouteRow = { key_label: null, provider: null, model: null, reasoning_effort: null };

function applyChange(row: RouteRow, change: RouteChange): RouteRow {
  switch (change.kind) {
    case 'model':
      return { ...row, provider: change.provider, model: change.model };
    case 'automatic':
      return row.model === null ? row : { ...row, provider: null, model: null };
    case 'keyLabel':
      return { ...row, key_label: change.keyLabel };
    case 'provider':
      return { ...row, provider: change.provider };
    case 'effort':
      return { ...row, reasoning_effort: change.effort };
  }
}

/** The field(s) a change sets or clears on purpose — never reported in `cleared`. */
function targetOf(change: RouteChange): readonly RouteField[] {
  switch (change.kind) {
    case 'model':
      return ['provider', 'model'];
    case 'automatic':
      return ['model'];
    case 'keyLabel':
      return ['key_label'];
    case 'provider':
      return ['provider'];
    case 'effort':
      return ['reasoning_effort'];
  }
}

/** The server's effort rule: an effort is checked against a pinned provider AND model. */
function storable(row: RouteRow): RouteRow {
  return row.reasoning_effort !== null && (row.provider === null || row.model === null)
    ? { ...row, reasoning_effort: null }
    : row;
}

function putBody(row: RouteRow): RoutePutBody {
  return {
    ...(row.key_label === null ? {} : { key_label: row.key_label }),
    ...(row.provider === null ? {} : { provider: row.provider }),
    ...(row.model === null ? {} : { model: row.model }),
    reasoning_effort: row.reasoning_effort,
  };
}

/**
 * The write for one change to a purpose's pin: the full intended row as a PUT, or a
 * DELETE when no pin axis survives. `current` null = the purpose has no row yet.
 */
export function nextRouteRow(current: RouteRow | null, change: RouteChange): NextRouteRow {
  const before = current ?? NO_ROW;
  const intended = applyChange(before, change);
  const row = storable(intended);
  const target = targetOf(change);
  // Emptied incidentally: set before (and not the change's own target), or asked for
  // by the change itself, and null in what is written.
  const cleared = ROUTE_FIELDS.filter(
    (f) => row[f] === null && (intended[f] !== null || (before[f] !== null && !target.includes(f))),
  );
  if (row.key_label === null && row.provider === null && row.model === null) {
    return { op: 'delete', cleared };
  }
  return { op: 'put', body: putBody(row), cleared };
}
